import React, { useCallback, useEffect } from "react";

import { cn } from "@perceptron/utils/cn";
import { Editor } from "@tiptap/react";

import { CaretBubblePosition } from "@demo/extensions/caret-bubble";
import { useIsMobile } from "@demo/hooks/useIsMobile";
import { useCaretBubbleStore } from "@demo/store/caret-bubble";
import { Role } from "@demo/types/base";
import { insertMediaSet } from "@demo/utils/insert-media-set";

import { CaretBubble } from "./CaretBubble";

interface EditorUIProps {
  editor: Editor | null;
  caretPosition: CaretBubblePosition | null;
  // Role of the editor the bubble belongs to; new media sets are registered under it
  role: Role;
  // Whether the bubble is in the DOM (so it can be reached with Tab): the editor is focused at a valid insertion point
  isAvailable?: boolean;
  // Whether the bubble is shown to the pointer (mouse near the caret); keyboard focus also reveals it
  isVisible?: boolean;
}

/**
 * Component that handles UI overlays for the editor
 * Single responsibility: Render the caret bubble that inserts a media container
 * Memoized to prevent unnecessary re-renders of UI elements
 */
export const EditorUI = React.memo(
  ({ editor, caretPosition, role, isAvailable = true, isVisible = true }: EditorUIProps) => {
    const isMobile = useIsMobile();
    const setBubbleFocused = useCaretBubbleStore((state) => state.setBubbleFocused);

    const isRendered = !!editor && !!caretPosition && isAvailable && !isMobile;

    // Unmounting a focused button fires no blur event, so clear the focus flag whenever the bubble goes away
    useEffect(() => {
      if (!isRendered && useCaretBubbleStore.getState().isBubbleFocused) {
        setBubbleFocused(false);
      }
    }, [isRendered, setBubbleFocused]);

    const handleBubbleClick = useCallback(() => {
      if (editor) insertMediaSet(editor, role);
    }, [editor, role]);

    const handleBubbleFocus = useCallback(() => setBubbleFocused(true), [setBubbleFocused]);
    const handleBubbleBlur = useCallback(() => setBubbleFocused(false), [setBubbleFocused]);

    if (!editor || !caretPosition || !isAvailable || isMobile) {
      return null;
    }

    return (
      <CaretBubble
        caretPosition={caretPosition}
        className={cn({
          // Out of pointer range: stay in the DOM so keyboard users can Tab to it, but only show it on focus
          "pointer-events-none opacity-0 focus-visible:pointer-events-auto focus-visible:opacity-100": !isVisible,
        })}
        editor={editor}
        onBlur={handleBubbleBlur}
        onClick={handleBubbleClick}
        onFocus={handleBubbleFocus}
      />
    );
  },
);

EditorUI.displayName = "EditorUI";

export default EditorUI;
